import { CardData, TextureData } from '../../types/types';
import { GameObjects } from 'phaser';
import { AppSettings } from '../managers/AppSettings';
import { GetBounds } from '../../utils/GetBounds';
import { OptionsDataManager } from '../managers/OptionsDataManager';
import { TrumpSuit } from '../state/TableStateMachine';
import { CardsManager } from '../managers/CardsManager';
import { AppTimings } from '../data/AppConstants';


/**
 * Effect that can be applied to card
 */
export enum CardEffect {
    /**
     * Normally looking, white card
     */
    NONE,
    /**
     * Darkening card overlay, makes card darkened
     */
    DARKEN
}

/**
 * Used when calculating card rank value, based on card rank and trump type (trump, and settings) (nowhere else).
 */
export enum TrumpMode {
    TRUMP_OTHER,
    NO_TRUMPS,
    ALL_TRUMPS,
    NO_TRUMPS_130,
    ALL_TRUMPS_258,
}

/**
 * 
 */
export class Card
    extends Phaser.GameObjects.Container {
    /**
     * Unique card id (only among cards)
     * - assigned upon creation (directly via public prop)
     * - allows to recognize specific card among other cards (each card has unique value)
     */
    // public id: number;

    /**
     * Card's suit
     */
    public suit: CardSuit;

    /**
     * Card's rank
     */
    public rank: CardRank;

    /**
     * Cached representation of card as CardData
     * (so 'new' doesnt have to be created each time)
     */
    public cardData: CardData;

    /**
     * Card's color
     */
    public color: CardColor;

    /**
     * 
     */
    private frontTextureData: TextureData;

    private backTextureData: TextureData;

    /**
     * Card shadow texture data
     */
    private shadowTextureData: TextureData;

    /**
     * Is card showing front?
     */
    private isFrontFacing: boolean;

    /**
     * Flag that notifies if setInteractive has been applied to this GameObject
     * Used in wrapper @see SetInteractive and in state check @see IsInteractive
     */
    private isInteractive: boolean = false;

    public view: GameObjects.Image;

    /**
     * natural shadowing on card
     */
    // public shadow: GameObjects.Image;

    /**
     * Dropshadow only when dragging card
     */
    public dropShadow: GameObjects.Image;

    /**
     * Currently used CardEffect
     */
    private curCardEffect: CardEffect = CardEffect.NONE;

    private effectToColor: Phaser.Display.Color[];


    /**
     * Dont call directly (via 'new'), instead use scene.add.card(..),
     * so that it doesnt fight with GameObjectFactory setup (which is a more complete setup)
     * @param cardData 
     * @param scene 
     * @param texture 
     */
    public constructor(scene: Phaser.Scene) {
        super(scene);
    }

    /**
     * Call immediately after constructor (after you instantiate the card)
     * @returns 
     */
    public Init(
        frontTextureData: TextureData,
        backTextureData: TextureData,
        shadowTextureData: TextureData,
        fadedOverlayTextureData: TextureData,
        rank: CardRank,
        suit: CardSuit,
        frontFacing: boolean): Card {
        this.frontTextureData = frontTextureData;
        this.backTextureData = backTextureData;
        this.shadowTextureData = shadowTextureData;
        this.suit = suit;
        this.rank = rank;
        this.cardData = { rank: this.rank, suit: this.suit };
        this.isFrontFacing = frontFacing;

        this.effectToColor = [];
        this.effectToColor[CardEffect.NONE] = new Phaser.Display.Color(255, 255, 255);
        const cardDarkenColor: Phaser.Types.Display.ColorObject = Phaser.Display.Color.ColorToRGBA(AppSettings.colors.cardDarkenColor);
        this.effectToColor[CardEffect.DARKEN] = new Phaser.Display.Color(cardDarkenColor.r, cardDarkenColor.g, cardDarkenColor.b);

        // color
        this.UpdateColor();
        //console.log(this.frontTextureData, this.backTextureData, this.suit, this.rank, this.color);

        return this;
    }

    public Build(): Card {
        if (this.shadowTextureData) {
            this.dropShadow = this.scene.make.image({
                key: this.shadowTextureData.texture,
                frame: this.shadowTextureData.frame,
            });
            this.add(this.dropShadow);
        }

        this.view = this.scene.make.image({
            key: this.isFrontFacing ? this.frontTextureData.texture : this.backTextureData.texture,
            frame: this.isFrontFacing ? this.frontTextureData.frame : this.backTextureData.frame
        });
        this.add(this.view);

        const cardHitArea: Phaser.Geom.Rectangle = GetBounds(this.view);
        cardHitArea.x -= this.view.displayWidth / 2;
        cardHitArea.y -= this.view.displayHeight / 2;
        this.setInteractive({
            hitArea: cardHitArea,
            hitAreaCallback: Phaser.Geom.Rectangle.Contains,
            draggable: false,
            useHandCursor: true
        });

        // by default, disable interactivity on cards
        this.disableInteractive();
        this.isInteractive = false;

        return this;
    }

    public IsTrump(trumpSuit: TrumpSuit): boolean {
        switch (trumpSuit) {
            case TrumpSuit.CLUBS:
                return this.suit == CardSuit.CLUBS;
            case TrumpSuit.DIAMONDS:
                return this.suit == CardSuit.DIAMONDS;
            case TrumpSuit.HEARTS:
                return this.suit == CardSuit.HEARTS;
            case TrumpSuit.SPADES:
                return this.suit == CardSuit.SPADES;
        }
        return false;
    }

    // public GetValue(trumpSuit: TrumpSuit, trumpMode: TrumpMode): number {
    //     switch (trumpMode) {
    //         case TrumpMode.TRUMP_OTHER:
    //             return this.IsTrump(trumpSuit) ? CardsManager.cardRankToCardProps[this.rank].valueTrump : CardsManager.cardRankToCardProps[this.rank].valueOther;
    //         case TrumpMode.NO_TRUMPS:
    //             return CardsManager.cardRankToCardProps[this.rank].valueNoTrumps;
    //         case TrumpMode.ALL_TRUMPS:
    //             return CardsManager.cardRankToCardProps[this.rank].valueAllTrumps;
    //         case TrumpMode.NO_TRUMPS_130:
    //             return CardsManager.cardRankToCardProps[this.rank].valueNoTrumps130;
    //         case TrumpMode.ALL_TRUMPS_258:
    //             return CardsManager.cardRankToCardProps[this.rank].valueAllTrumps258;
    //     }
    // }

    public GetRank(trumpMode: TrumpMode, trumpSuit?: TrumpSuit): number {
        switch (trumpMode) {
            case TrumpMode.TRUMP_OTHER:
                return this.IsTrump(trumpSuit) ?
                    CardsManager.cardRankToCardProps[this.rank].rankOrderTrump :
                    CardsManager.cardRankToCardProps[this.rank].rankOrderOther;
            case TrumpMode.NO_TRUMPS:
                return CardsManager.cardRankToCardProps[this.rank].rankOrderNoTrumps;
            case TrumpMode.ALL_TRUMPS:
                return CardsManager.cardRankToCardProps[this.rank].rankOrderAllTrumps;
            case TrumpMode.NO_TRUMPS_130:
                return CardsManager.cardRankToCardProps[this.rank].rankOrderNoTrumps130;
            case TrumpMode.ALL_TRUMPS_258:
                return CardsManager.cardRankToCardProps[this.rank].rankOrderAllTrumps258;
        }
    }

    public GetCardData(): CardData {
        return this.cardData;
    }

    /**
     * Debug
     * Log card id/name
     */
    public log(): string {
        return `card-${CardSuit[this.suit]}-${CardRank[this.rank]}`;
    }

    /**
     * Reset to default state
     * - before pooling into CardsManager
     * 
     * Otherwords: Some props are being animated externally, but we reset them here
     */
    public Reset(): void {
        this.SetInteractive(false);
        this.SetEffect(CardEffect.NONE, false); // animate?
        // 
        this.alpha = 1;
        this.scale = 1;

        this.scene.tweens.killTweensOf(this);
        this.scene.tweens.killTweensOf(this.view);
        this.scene.tweens.killTweensOf(this.dropShadow);
        this.view.scaleX = 1;
        this.dropShadow.scaleX = 1;
    }

    public SetEffect(cardEffect: CardEffect, animate: boolean = true): void {
        if (this.curCardEffect != cardEffect) {
            // console.log(this.log());
            const toColor: Phaser.Display.Color = this.effectToColor[cardEffect];

            if (animate) {
                const fromColor: Phaser.Display.Color = this.effectToColor[this.curCardEffect];
                const cardHighlightDuration = OptionsDataManager.GetTiming(AppTimings.cardHighlightDuration);
                const cardEffectTween = this.scene.tweens.addCounter({
                    duration: cardHighlightDuration,
                    from: 0,
                    to: 100,
                    // onStart: ()=>{
                    //     this.view.setTint(this.effectToColor[cardEffect].color);
                    // },
                    onUpdate: () => {
                        // Interpolate between the fromColor and toColor
                        // using the current tween value.
                        var tint = Phaser.Display.Color.Interpolate.ColorWithColor(
                            fromColor, toColor, 100, cardEffectTween.getValue()
                        );
                        // Interpolate.ColorWithColor returns a Javascript object with
                        // interpolated RGB values. We convert it to a Phaser.Display.Color object
                        // in order to get the integer value of the tint color.
                        const deltaColor: number = Phaser.Display.Color.ObjectToColor(tint).color;
                        // apply same intermediate color to all 4 vertices
                        this.view.setTint(deltaColor);
                    },
                    onComplete: () => {
                        if (cardEffect == CardEffect.NONE) {
                            this.view.clearTint();
                        }
                    },
                });
            }
            else {
                this.view.setTint(toColor.color);
            }

            this.curCardEffect = cardEffect;
        }
    }

    public GetEffect(): CardEffect {
        return this.curCardEffect;
    }

    public SetBackTextureData(backTextureData: TextureData, updateView: boolean = false): void {
        this.backTextureData = backTextureData;
        if (updateView) {
            this.UpdateView();
        }
    }

    public IsFrontFacing(): boolean {
        return this.isFrontFacing;
    }

    /**
     * Wrapper for @see setInteractive() (no arguments) and @see disableInteractive()
     * Also allows us to use @see IsInteractive to avoid enabling/disabling if already enabled/disabled
     * @param interactive True:  GameObject is set interactive (receives mouse/touch/.. events)
     *                    False: GameObject is set to not be interactive
     */
    public SetInteractive(setInteractive: boolean): void {
        // console.log("Card::SetInteractive;", CardRank[this.rank],CardSuit[this.suit], this.isInteractive,">",setInteractive)
        if (this.isInteractive != setInteractive) {
            if (setInteractive) {
                this.setInteractive({
                    hitArea: GetBounds(this.view),
                    hitAreaCallback: Phaser.Geom.Rectangle.Contains,
                    draggable: true,
                    useHandCursor: true
                });
                // NOTE: on Card, via getBounds(), the debug Rect displays offset, but works ok;
                // this.scene.input.enableDebug(this);
            }
            else {
                this.disableInteractive();
            }
            this.isInteractive = setInteractive;
        }
    }

    public IsInteractive(): boolean {
        return this.isInteractive;
    }

    public UpdateColor(): CardColor {
        if (this.suit === CardSuit.CLUBS || this.suit === CardSuit.SPADES) {
            this.color = CardColor.BLACK;
        }
        else {
            this.color = CardColor.RED;
        }
        return this.color;
    }

    /**
     * Flips frontFacing, updates view and interactivity of the Card
     * 
     * @param animProps if left undefined, flips instantly (no animation)
     * @param endScale 
     * @param onCompleteScale onComplete, set a different scale?
     */
    public Flip(animate: boolean = false, dealDuration: number = 300): void {
        this.scene.tweens.killTweensOf(this.view);
        this.scene.tweens.killTweensOf(this.dropShadow);
        if (animate) {
            const halfDuration = dealDuration / 2;
            // first scale to 0
            this.scene.tweens.add({
                targets: [this.view, this.dropShadow],
                scaleX: 0,
                duration: halfDuration,
                onComplete: () => {
                    this.SetFrontFacing(!this.isFrontFacing);
                    this.scene.tweens.add({
                        targets: [this.view, this.dropShadow],
                        scaleX: 1,
                        duration: halfDuration,
                    });
                },
            })
        }
        else {
            this.view.scaleX = this.dropShadow.scaleX = 1;
            this.SetFrontFacing(!this.isFrontFacing);
        }
    }

    /**
     * Set card to be front facing or back facing
     * NOTE: Helper function. To simply Flip a card, use @see Flip
     * @param newFacing 
     * @returns returns Card for chaining
     */
    public SetFrontFacing(isFrontFacing: boolean): Card {
        if (isFrontFacing !== this.isFrontFacing) {
            this.isFrontFacing = isFrontFacing;
            this.UpdateView();
        }
        return this;
    }

    /**
     * Update view from change in frontTextureData/backTextureData/texture/frame/isFrontFacing
     * EDIT: now public @see AppAnimationsManager.AnimateMove
     */
    public UpdateView(): void {
        if (this.isFrontFacing) {
            // NOTE: setTexture vs setFrame speed is practically equal
            this.view.setTexture(this.frontTextureData.texture, this.frontTextureData.frame);
        }
        else {
            this.view.setTexture(this.backTextureData.texture, this.backTextureData.frame);
        }
    }
}

/**
 * Note: for suit order (in hand), @see CardsManager.suitOrder
 * NOTE: ties directly to card deck atlas frames! Do not change!
 */
export enum CardSuit {
    SPADES,     // symbol: ♠
    CLUBS,      // symbol: ♣
    DIAMONDS,   // symbol: ♦
    HEARTS,     // symbol: ♥
}

/**
 * In Belote, we use 'rank' as 'key' (numerical key = index in cardProps) for 'Order' and 'Value'
 * NOTE: Do not change this order. 
 *       Major Declarations Sequences use this order. 
 *       It is always: 7, 8, 9, 10, Jack, Queen, King, Ace 
 *       (no other (NT/AT/Trump/Other) rank ordering follows this order, so it can't be reused elsewhere).
 */
export enum CardRank {
    SEVEN,  // 7    index: 0
    EIGHT,  // 8    index: 1
    NINE,   // 9    index: 2
    TEN,    // 10   index: 3
    JACK,   // J    index: 4
    QUEEN,  // Q    index: 5
    KING,   // K    index: 6
    ACE,    // A    index: 7
}

export enum CardColor {
    RED,
    BLACK
}

export enum OtherSuitCardsOrder {
    SEVEN,  // 7    index: 0
    EIGHT,  // 8    index: 1
    NINE,   // 9    index: 2
    JACK,   // J    index: 3
    QUEEN,  // Q    index: 4
    KING,   // K    index: 5
    TEN,    // 10   index: 6
    ACE,    // A    index: 7
}

export enum TrumpSuitCardsOrder {
    SEVEN,  // 7    index: 0
    EIGHT,  // 8    index: 1
    QUEEN,  // Q    index: 2
    KING,   // K    index: 3
    TEN,    // 10   index: 4
    ACE,    // A    index: 5
    NINE,   // 9    index: 6
    JACK,   // J    index: 7
}